// csvUtils.ts - Centralized utility for CSV operations
import Papa from 'papaparse';

type ParseOptions = {
  header?: boolean;
  skipEmptyLines?: boolean;
  complete?: (results: any) => void;
  error?: (error: Error) => void;
};

/**
 * Parse a CSV file and return the headers and data
 * @param file The CSV file to parse
 * @returns A promise that resolves to an object with headers and data
 */
export const parseCSV = (file: File): Promise<{ headers: string[]; data: any[] }> => {
  return new Promise((resolve, reject) => {
    Papa.parse<any>(file as Papa.LocalFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results: Papa.ParseResult<any>) => {
        resolve({
          headers: results.meta.fields || [],
          data: results.data
        });
      },
      error: (error: Error) => {
        console.error('CSV parsing error:', error);
        reject(error);
      }
    });
  });
};

/**
 * Generate and download a CSV template with the provided headers
 * @param headers Array of header names for the CSV
 * @param filename Name of the file to download
 */
export const generateCSVTemplate = (headers: string[], filename: string = 'template.csv'): void => {
  // Create a single row with headers
  const csv = Papa.unparse({
    fields: headers,
    data: [headers.reduce((acc, header) => ({ ...acc, [header]: '' }), {})]
  });
  
  // Create a Blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Find automatically matching fields between source and target arrays
 * Uses fuzzy matching on field names
 * 
 * @param sourceFields Array of field names from CSV
 * @param targetFields Array of field objects with name properties
 * @returns Object with mappings from target to source fields
 */
export const findMatchingFields = (
  sourceFields: string[], 
  targetFields: Array<{ name: string; similarNames?: string[] }>
): Record<string, string> => {
  const mapping: Record<string, string> = {};

  // Try to automatically map fields with the same or similar names
  targetFields.forEach(targetField => {
    // Check for exact matches first
    const exactMatch = sourceFields.find(
      sourceField => sourceField.toLowerCase().trim() === targetField.name.toLowerCase().trim()
    );
    
    if (exactMatch) {
      mapping[targetField.name] = exactMatch;
      return;
    }
    
    // Then check for similarity based on includes
    const similarMatch = sourceFields.find(
      sourceField => 
        sourceField.toLowerCase().includes(targetField.name.toLowerCase()) ||
        targetField.name.toLowerCase().includes(sourceField.toLowerCase())
    );
    
    if (similarMatch) {
      mapping[targetField.name] = similarMatch;
      return;
    }
    
    // Finally check for any provided similar names
    if (targetField.similarNames && targetField.similarNames.length > 0) {
      for (const similarName of targetField.similarNames) {
        const match = sourceFields.find(
          sourceField => sourceField.toLowerCase().trim() === similarName.toLowerCase().trim()
        );
        
        if (match) {
          mapping[targetField.name] = match;
          return;
        }
      }
    }
  });

  return mapping;
};

/**
 * Validate the mapped fields against required fields
 * @param mapping The current field mapping
 * @param requiredFields Array of field names that are required
 * @returns Object with validation results
 */
export const validateMapping = (
  mapping: Record<string, string>, 
  requiredFields: string[]
): { isValid: boolean; missingFields: string[] } => {
  const missingFields = requiredFields.filter(
    field => !mapping[field] || mapping[field] === "Don't import"
  );
  
  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};